/**
 * FilterBar — peace-hub-port (Block 6.F.2 §3.3; TypeScript per UX-13 S1).
 *
 * UX-13 S1: the hand-rolled chipClasses pill grammar is retired in favor
 * of the FilterPill primitive (which was promoted FROM this grammar with
 * the geometry ruling applied): rounded-base at the ruled density, the
 * count as a badge rather than a "(N)" text suffix, no chevron (the
 * primitive is the trigger; the BottomSheet is the affordance). The
 * "Clear" action button stays hand-rolled (it is an action, not a filter
 * state; the S5 raw-button burn-down owns it).
 *
 * Adapted port of peace-hub/src/components/FilterBar.jsx with
 * adaptations:
 *   - Wraps the inline bottom-sheet pattern in <BottomSheet> primitive
 *     (§3.1).
 *   - filterOptions JSON source: peace-hub preloads at build time;
 *     peace-os derives from `resources` array via the cascade memo.
 *     Single source of truth: bulk-load resources.
 *   - URL slug ↔ display-name resolution: each peace-os resource row
 *     ships {slug, display_name} on subcategories[] / specialties[];
 *     FilterBar maps display↔slug via these inline. Counties is
 *     derived flat string[] (M5; peace-hub-style).
 *
 * §3.5: STATE filter chip from old Directory.tsx is DROPPED — Phase 1
 * single-state implicit per ADR-019. Era B reintroduces with multi-
 * state data.
 *
 * Cascading availableOptions (§2.5 + §3.3): pure derivation from
 * `filters.categories + raw resources`. NOT visible result set.
 * Single-level cascade (selecting subcategory does NOT further narrow
 * specialty/county). Matches peace-hub verbatim.
 *
 * Phase 1.9 introduced the `highlights` pill (multi-select; mirrors
 * Specialty's code path; sourced from r.highlights — the parallel
 * provider_highlights taxonomy from migrations 0066+0067).
 *
 * Phase 1.9.1 rebuilt the `highlights` pill:
 *   - Renamed UI label "Highlights" → "Features" (internal
 *     DROPDOWN_FILTERS key stays 'highlights' for minimum diff).
 *   - Data source swapped: read from r.medicaid + r.dementia_unit
 *     (A5-cohort columns from migration 0058) instead of r.highlights.
 *   - Grouped picker with two family sub-headers (Medicaid + Memory Care).
 *   - The §3.3 §m / Q-S2 (a) "schema gap" anchor that previously hid
 *     medicaid + dementia_unit chips is CLOSED — those columns shipped
 *     in migration 0058 and now drive the Features pill directly.
 *   - provider_highlights tables stay in place (orphaned for this
 *     filter; preserved for future CM-managed tags).
 */

import { useState, useMemo, type ReactNode } from 'react'
import { Check, X } from 'lucide-react'
import { BottomSheet } from '@/components/primitives/BottomSheet'
import { PAGE_WIDTH_CLASSES, PAGE_GUTTER } from '@/components/primitives/PageShell'
import { FilterPill } from '@/components/primitives/FilterPill'
import { SearchField } from '@/components/primitives/SearchField'
import { FILTER_LABELS, categoryDisplayFromSlug } from '@/lib/uiCategoryLabels'
import { formatCountyDisplay } from '@/lib/serviceAreas'
import { orderFacetEntries, usePinnedSelection } from '@/lib/facetOrder'
import type { IndividualForSearch, ProviderForSearch } from '@/lib/types'
// SSOT — the facet option lists live in one place (shared by the suggest
// form, the org detail edit page, and the filter dispatch). Re-exported below
// so existing `from '@/components/peace-hub-port/FilterBar'` consumers keep
// working.
import {
  FEATURES_FILTER_GROUPS,
  DELIVERY_MODEL_LOCATIONS,
  HOME_CARE_TYPE_LABELS,
  RESPITE_DISPLAY_LABEL,
  RESPITE_MODEL_VALUE,
  MEMORY_CARE_LABEL,
  RESTRICTIONS_LABELS,
} from '@/lib/facetTaxonomy'

export {
  FEATURES_FILTER_GROUPS,
  DELIVERY_MODEL_LOCATIONS,
  HOME_CARE_TYPE_LABELS,
  RESPITE_DISPLAY_LABEL,
  RESPITE_MODEL_VALUE,
  MEMORY_CARE_LABEL,
  RESTRICTIONS_LABELS,
}

// Facet option lists moved to @/lib/facetTaxonomy (imported + re-exported
// above) so the suggest form, the detail edit page, the filter dispatch, and
// these filter pills all read one source. Behavior is unchanged; the values
// are identical to the prior inline definitions.

/**
 * The directory filter-state shape maintained by useUnifiedSearch (still
 * plain JS): string[] facets keyed by dimension + the has_notes boolean.
 * Typed here at the boundary per UX-13 S1.
 */
export interface DirectoryFilters {
  categories?: string[]
  subcategories?: string[]
  specialties?: string[]
  highlights?: string[]
  service_models?: string[]
  payment_types?: string[]
  counties?: string[]
  contact_specialties?: string[]
  has_notes?: boolean
  // The tenant's curation flag (is_recommended) on both directories. A
  // toggle, not a sort: the lists and the map show ONLY recommended
  // organizations and people while it is on. Round-trips as
  // ?recommended=1.
  recommended_only?: boolean
  // People view only: the manual is_referral_partner flag (0019, the
  // Airtable-imported CRM signal; org-side dropped in 0059 as individual-
  // only). Surfaced as a filter + badge, not derived.
  is_referral_partner?: boolean
}

type DropdownFilterKey =
  | 'categories'
  | 'subcategories'
  | 'specialties'
  | 'highlights'
  | 'service_models'
  | 'payment_types'
  | 'counties'

const DROPDOWN_FILTERS: DropdownFilterKey[] = ['categories', 'subcategories', 'specialties', 'highlights', 'service_models', 'payment_types', 'counties']

/** [display_name, count] entries — peace-hub's filterOptions.json shape. */
type OptionEntry = [string, number | null]

interface FilterDropdownProps {
  filterKey: string
  isOpen: boolean
  onClose: () => void
  selected: string[]
  onToggle: (filterKey: string, name: string) => void
  onClear: (filterKey: string) => void
  allOptions: OptionEntry[]
  availableOptions: Set<string> | null
  staticItems?: ReadonlyArray<string>
  groupedItems?: ReadonlyArray<{ family: string; items: ReadonlyArray<string> }> | null
}

function FilterDropdown({ filterKey, isOpen, onClose, selected, onToggle, onClear, allOptions, availableOptions, staticItems, groupedItems }: FilterDropdownProps) {
  // Phase 1.10 scope-add — per-pill search state. The previous shape
  // persisted search across pill opens (FilterDropdown unmounts when
  // isOpen=false; re-mount initializes state to ''), so this useState
  // already resets on cross-pill switches via React's standard
  // mount-on-open shape. The × affordance below adds an explicit clear
  // gesture for in-pill clearing (without closing the sheet).
  const [search, setSearch] = useState('')

  // Phase 1.9.1 — when groupedItems is passed (Features pill), render
  // sub-header + sub-items per family. Otherwise the existing flat-list
  // shape (staticItems OR allOptions-from-cascade) carries forward
  // unchanged for the other 4 pills.
  const allItems: OptionEntry[] = staticItems
    ? staticItems.map((name) => [name, null])
    : (allOptions || []).slice().sort((a, b) => a[0].localeCompare(b[0]))

  // Cascade only for non-static, non-grouped dropdowns.
  const items = (availableOptions && !staticItems && !groupedItems)
    ? allItems.filter(([name]) => availableOptions.has(name))
    : allItems

  // The facet-list order (src/lib/facetOrder.ts): checked entries first,
  // within their group on the grouped sheet, taken when the sheet opens or
  // changes and on each change to the search, never on a click. While a
  // search is typed the checked entries stay visible above the matches.
  const { pinned, markOwn } = usePinnedSelection(selected, `${filterKey}|${isOpen ? 'open' : 'shut'}|${search}`)
  const toggle = (name: string) => {
    markOwn(selected.includes(name) ? selected.filter((v) => v !== name) : [...selected, name])
    onToggle(filterKey, name)
  }
  const ownName = (name: string) => name
  const filtered = orderFacetEntries(items, ([name]) => name, ([name]) => name, pinned, search)
  const filteredGroups = groupedItems
    ? groupedItems
        .map((g) => ({ ...g, items: orderFacetEntries(g.items, ownName, ownName, pinned, search) }))
        .filter((g) => g.items.length > 0)
    : null

  const headerRight = selected.length > 0
    ? (
      <button
        onClick={() => onClear(filterKey)}
        className="text-warm-400 text-sm font-semibold cursor-pointer"
      >
        Clear ({selected.length})
      </button>
    )
    : null

  // Render a single checkbox row. Shared by flat + grouped renderers.
  const renderRow = (name: string) => {
    const isSelected = selected.includes(name)
    return (
      <div
        key={name}
        onClick={() => toggle(name)}
        className="flex items-center gap-3 py-2.5 border-b border-sage-800/[8%] last:border-0 cursor-pointer"
      >
        <div
          className={`w-[22px] h-[22px] rounded-md border-2 flex items-center justify-center shrink-0 transition-colors ${
            isSelected
              ? 'bg-sage-800 border-sage-800 text-cream'
              : 'border-sage-300'
          }`}
        >
          {isSelected && <Check size={14} aria-hidden="true" />}
        </div>
        {/* Block 6.G.1 hotfix M1 — strip ", XX" state suffix on
            counties only.
            Block 6.G.2 B2 — map category slugs to display names
            (e.g. 'medical-practice' → 'Medical Practice') via the
            CATEGORY_SLUG_TO_DISPLAY shared constant. Same conditional
            gating discipline: subcategories/specialties already
            carry display_names; only categories + counties need
            per-dimension render-time mapping. Search input below
            preserved unchanged — matches against raw display_name /
            raw slug so user typing "medical" still surfaces
            medical-practice rows (render-time-strip-only discipline). */}
        <span className="text-sm text-ink">
          {filterKey === 'counties'
            ? formatCountyDisplay(name)
            : filterKey === 'categories'
              ? (categoryDisplayFromSlug(name) ?? name)
              : name}
        </span>
      </div>
    )
  }

  return (
    <BottomSheet
      isOpen={isOpen}
      onClose={onClose}
      title={FILTER_LABELS[filterKey] ?? filterKey}
      headerRight={headerRight}
    >
      {/* The sheet's search is the house SearchField. Mount on open gives
          each pill a fresh, empty search; the field's clear button empties
          it inside the sheet. */}
      <SearchField
        value={search}
        onChange={setSearch}
        placeholder="Search this list"
        autoFocus
        aria-label={`Search within ${FILTER_LABELS[filterKey] ?? filterKey}`}
        testId="filter-sheet-search"
        className="mx-5 mt-3"
      />

      <div className="px-5 pt-2 pb-5">
        {filteredGroups
          ? filteredGroups.map((group) => (
              <div key={group.family} className="pt-3 first:pt-0">
                {/* Phase 1.9.1 — family sub-header. Sage-700 small-caps;
                    consistent with the existing chrome's color palette. */}
                <div className="text-[11px] font-semibold uppercase tracking-wider text-sage-700 pb-1.5">
                  {group.family}
                </div>
                {group.items.map((name) => renderRow(name))}
              </div>
            ))
          : filtered.map(([name]) => renderRow(name))}
      </div>
    </BottomSheet>
  )
}

export interface FilterBarProps {
  filters: DirectoryFilters
  resources: ProviderForSearch[] | null | undefined
  onToggleFilter: (filterKey: string, name: string) => void
  onToggleBool: (filterKey: string) => void
  onClearFilter: (filterKey: string) => void
  onClearAll: () => void
  hasActiveFilters: boolean
}

// Phase 1.9.1 — medicaid + dementia_unit reactivated via the Features
// pill (grouped picker; data source is r.medicaid + r.dementia_unit).
// useUnifiedSearch's existing `filters.highlights: string[]` shape is
// reused for the selection state — labels are unique across families
// so a flat list works; dispatch to the right column happens at filter
// application time in useUnifiedSearch.js.
export function FilterBar({
  filters,
  resources,
  onToggleFilter,
  onToggleBool,
  onClearFilter,
  onClearAll,
  hasActiveFilters,
}: FilterBarProps): ReactNode {
  const [openDropdown, setOpenDropdown] = useState<DropdownFilterKey | null>(null)

  // Build full option lists from raw resources (single SoT). Each entry
  // is [display_name, count] to match peace-hub's filterOptions.json shape.
  const allOptions = useMemo<Record<DropdownFilterKey, OptionEntry[]>>(() => {
    if (!resources) return { categories: [], subcategories: [], specialties: [], highlights: [], service_models: [], payment_types: [], counties: [] }
    const cats = new Map<string, number>()
    const subs = new Map<string, number>()
    const specs = new Map<string, number>()
    const highs = new Map<string, number>()
    const sdms = new Map<string, number>()
    const pays = new Map<string, number>()
    const counts = new Map<string, number>()
    for (const r of resources) {
      // categories: peace-os has category_slug; FilterBar uses display via uiCategoryLabels
      // For consistency with peace-hub UX, surface the display-name from CATEGORY_SLUG_TO_DISPLAY.
      // For simplicity, inline the slug here; consumer can map via uiCategoryLabels if needed.
      cats.set(r.category_slug, (cats.get(r.category_slug) ?? 0) + 1)
      for (const s of r.subcategories) {
        subs.set(s.display_name, (subs.get(s.display_name) ?? 0) + 1)
      }
      for (const s of r.specialties) {
        specs.set(s.display_name, (specs.get(s.display_name) ?? 0) + 1)
      }
      // Phase 1.9 — highlights option list. Mirrors specialties construction.
      // r.highlights may be undefined transiently (older cached payloads
      // pre-Phase-1.9); ?? [] guards.
      for (const s of (r.highlights ?? [])) {
        highs.set(s.display_name, (highs.get(s.display_name) ?? 0) + 1)
      }
      // Phase 1.10 — service_models option list. Source flipped from
      // the single-value scalar to the plural `r.service_delivery_models:
      // string[]` per r2 disposition #11. Count is now "number of orgs
      // with that model assigned" not "number of orgs with that model as
      // their only model" — material change in count semantics but
      // correct for the picker (lists distinct vocabulary).
      for (const m of (r.service_delivery_models ?? [])) {
        sdms.set(m, (sdms.get(m) ?? 0) + 1)
      }
      // Payment type option list (provider_organizations.payment_types jsonb
      // string[]). Mirrors service_models: distinct values present in the data,
      // counted; no invented/empty options. ?? [] guards older cached payloads.
      for (const p of (r.payment_types ?? [])) {
        pays.set(p, (pays.get(p) ?? 0) + 1)
      }
      for (const c of r.counties) {
        counts.set(c, (counts.get(c) ?? 0) + 1)
      }
    }
    return {
      categories: [...cats.entries()],
      subcategories: [...subs.entries()],
      specialties: [...specs.entries()],
      highlights: [...highs.entries()],
      service_models: [...sdms.entries()],
      payment_types: [...pays.entries()],
      counties: [...counts.entries()],
    }
  }, [resources])

  // Cascading availableOptions (§2.5): derive from filters.categories +
  // raw resources only. Matches peace-hub FilterBar.jsx:96-104 verbatim
  // shape (counties via M5 derived field).
  const availableOptions = useMemo(() => {
    if (!filters.categories?.length || !resources) return null
    const filtered = resources.filter((r) => filters.categories!.includes(r.category_slug))
    return {
      subcategories: new Set(filtered.flatMap((r) => r.subcategories.map((s) => s.display_name))),
      specialties: new Set(filtered.flatMap((r) => r.specialties.map((s) => s.display_name))),
      // Phase 1.9 — highlights cascade. Mirrors specialties.
      highlights: new Set(filtered.flatMap((r) => (r.highlights ?? []).map((s) => s.display_name))),
      // Phase 1.10 — service_models cascade. Plural string[] flat-mapped
      // per r2 disposition #11.
      service_models: new Set(filtered.flatMap((r) => r.service_delivery_models ?? [])),
      payment_types: new Set(filtered.flatMap((r) => r.payment_types ?? [])),
      counties: new Set(filtered.flatMap((r) => r.counties)),
    }
  }, [filters.categories, resources])

  return (
    <>
      <div data-testid="directory-filter-row" className="border-y border-sage-800/[8%] py-3 overflow-x-auto scrollbar-none lg:overflow-visible">
        {/* A phone scrolls the row sideways inside this container, so the
            page never does; from lg the row wraps (two lines at most). */}
        <div className={`${PAGE_WIDTH_CLASSES.wide} mx-auto ${PAGE_GUTTER} flex items-center gap-2 min-w-max lg:min-w-0 lg:flex-wrap`}>
          <span className="shrink-0 pr-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-sage-400">
            Filter
          </span>
          {DROPDOWN_FILTERS.map((key) => {
            const count = filters[key]?.length || 0
            // Phase 1.9.1 — 'highlights' key renders as "Features" pill.
            // Internal key stays 'highlights' for minimum-diff (selection
            // state shape unchanged; filter dispatch handled per-label in
            // useUnifiedSearch.js). FILTER_LABELS['highlights'] also
            // resolves to "Features" for the picker bottom-sheet title.
            const label = key === 'categories' ? 'Category'
              : key === 'subcategories' ? 'Type'
                : key === 'specialties' ? 'Specialty'
                  : key === 'highlights' ? 'Features'
                    : key === 'service_models' ? 'Delivery method'
                      : key === 'payment_types' ? 'Payment type'
                        : 'County'
            return (
              <FilterPill
                key={key}
                label={label}
                count={count}
                active={count > 0}
                onClick={() => setOpenDropdown(key)}
              />
            )
          })}

          {/* E2 — "Has notes" boolean toggle (providers with ≥1 team note).
              Standalone pill, not a dropdown; round-trips via ?has_notes=1. */}
          <FilterPill
            label="Has notes"
            active={!!filters.has_notes}
            icon={<Check size={13} aria-hidden="true" className={filters.has_notes ? '' : 'opacity-40'} />}
            onClick={() => onToggleBool('has_notes')}
          />

          {/* "Recommended" boolean toggle: only the tenant's recommended
              organizations (is_recommended). The same pill drives the list
              and the map view; round-trips via ?recommended=1. */}
          <FilterPill
            label="Recommended"
            active={!!filters.recommended_only}
            icon={<Check size={13} aria-hidden="true" className={filters.recommended_only ? '' : 'opacity-40'} />}
            onClick={() => onToggleBool('recommended_only')}
            data-testid="filter-recommended-only"
          />

          {hasActiveFilters && (
            <button
              onClick={onClearAll}
              className="inline-flex shrink-0 items-center gap-1 rounded-pill border border-sage-800/[8%] px-3.5 py-[7px] text-[13px] font-medium text-ink-soft transition-colors duration-[var(--dur-base)] ease-quiet hover:bg-sand hover:text-sage-800 cursor-pointer whitespace-nowrap focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400"
            >
              <X size={13} aria-hidden="true" /> Clear
            </button>
          )}
        </div>
      </div>

      <FilterDropdown
        filterKey={openDropdown ?? 'categories'}
        isOpen={openDropdown !== null}
        onClose={() => setOpenDropdown(null)}
        selected={(openDropdown ? filters[openDropdown] : undefined) ?? []}
        onToggle={onToggleFilter}
        onClear={onClearFilter}
        allOptions={openDropdown ? allOptions[openDropdown] : []}
        availableOptions={
          openDropdown && openDropdown !== 'categories' && openDropdown !== 'highlights'
            ? availableOptions?.[openDropdown] ?? null
            : null
        }
        // Phase 1.9.1 — Features pill renders the grouped picker shape;
        // sub-items are static (sourced from FEATURES_FILTER_GROUPS, not
        // cascaded from resources). Other pills carry through with the
        // existing flat-list shape.
        groupedItems={openDropdown === 'highlights' ? FEATURES_FILTER_GROUPS : null}
        // E3 (Option 1) — the "Delivery method" pill shows ONLY the 5 location
        // values (static); hourly/24-7/live-in/respite still live in
        // service_delivery_models but are surfaced under Features, not here.
        staticItems={openDropdown === 'service_models' ? DELIVERY_MODEL_LOCATIONS : undefined}
      />
    </>
  )
}

export interface ContactFilterBarProps {
  contacts: IndividualForSearch[] | null | undefined
  filters: DirectoryFilters
  onToggleFilter: (filterKey: string, name: string) => void
  onToggleBool: (filterKey: string) => void
  onClearFilter: (filterKey: string) => void
  onClearAll: () => void
  hasActiveFilters: boolean
}

// ContactFilterBar — the individuals-directory (people view) filter row. The
// FilterBar above is org-faceted + org-sourced; the people view has no filters
// today, so this adds its two contact facets, mirroring the shared chrome +
// reusing FilterDropdown (no new filter primitive):
//   - "Has notes" boolean (filters.has_notes — the same key the org has-notes
//     chip uses; predicate in useUnifiedSearch keys on the contact's notes).
//   - "Specialties" multi-select (filters.contact_specialties) cascaded from
//     the contacts' comma-joined controlled-vocab specialty tokens (de-duped
//     case-insensitively, first-seen casing for display, counted per contact).
export function ContactFilterBar({
  contacts,
  filters,
  onToggleFilter,
  onToggleBool,
  onClearFilter,
  onClearAll,
  hasActiveFilters,
}: ContactFilterBarProps): ReactNode {
  const [open, setOpen] = useState(false)

  const specialtyOptions = useMemo<OptionEntry[]>(() => {
    const m = new Map<string, [string, number]>() // lowercased token -> [display, count]
    for (const c of contacts ?? []) {
      const seen = new Set<string>()
      for (const raw of (c.specialties ?? '').split(',')) {
        const t = raw.trim()
        if (!t) continue
        const key = t.toLowerCase()
        if (seen.has(key)) continue
        seen.add(key)
        const existing = m.get(key)
        if (existing) existing[1] += 1
        else m.set(key, [t, 1])
      }
    }
    return [...m.values()]
  }, [contacts])

  const specialtyCount = filters.contact_specialties?.length || 0

  return (
    <>
      <div data-testid="directory-filter-row" className="border-y border-sage-800/[8%] py-3 overflow-x-auto scrollbar-none lg:overflow-visible">
        {/* A phone scrolls the row sideways inside this container, so the
            page never does; from lg the row wraps (two lines at most). */}
        <div className={`${PAGE_WIDTH_CLASSES.wide} mx-auto ${PAGE_GUTTER} flex items-center gap-2 min-w-max lg:min-w-0 lg:flex-wrap`}>
          <span className="shrink-0 pr-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-sage-400">
            Filter
          </span>
          <FilterPill
            label="Specialties"
            count={specialtyCount}
            active={specialtyCount > 0}
            onClick={() => setOpen(true)}
          />
          <FilterPill
            label="Has notes"
            active={!!filters.has_notes}
            icon={<Check size={13} aria-hidden="true" className={filters.has_notes ? '' : 'opacity-40'} />}
            onClick={() => onToggleBool('has_notes')}
          />
          <FilterPill
            label="Referral partner"
            active={!!filters.is_referral_partner}
            icon={
              <Check
                size={13}
                aria-hidden="true"
                className={filters.is_referral_partner ? '' : 'opacity-40'}
              />
            }
            onClick={() => onToggleBool('is_referral_partner')}
          />
          {/* The same Recommended toggle the organization bar carries:
              only recommended people, on the list and the map. */}
          <FilterPill
            label="Recommended"
            active={!!filters.recommended_only}
            icon={<Check size={13} aria-hidden="true" className={filters.recommended_only ? '' : 'opacity-40'} />}
            onClick={() => onToggleBool('recommended_only')}
            data-testid="filter-recommended-only"
          />
          {hasActiveFilters && (
            <button
              onClick={onClearAll}
              className="inline-flex shrink-0 items-center gap-1 rounded-pill border border-sage-800/[8%] px-3.5 py-[7px] text-[13px] font-medium text-ink-soft transition-colors duration-[var(--dur-base)] ease-quiet hover:bg-sand hover:text-sage-800 cursor-pointer whitespace-nowrap focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400"
            >
              <X size={13} aria-hidden="true" /> Clear
            </button>
          )}
        </div>
      </div>

      <FilterDropdown
        filterKey="contact_specialties"
        isOpen={open}
        onClose={() => setOpen(false)}
        selected={filters.contact_specialties ?? []}
        onToggle={onToggleFilter}
        onClear={onClearFilter}
        allOptions={specialtyOptions}
        availableOptions={null}
      />
    </>
  )
}
